/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import { ConfirmModal } from "@advapacs/common";
import { useAppDispatch } from "@src/config/store";
import { getUser } from "@src/pages/unauthenticated/ducks/operations";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import CheckConnectedIcon from "@src/assets/icons/CheckCircle.svg";
import { redirectLoginPage } from "@src/utils/utils";

const OauthCallback: FC = () => {
  const dispatch = useAppDispatch();
  const pageLoaded = useRef(false);
  const location = useLocation();
  const [type, setType] = useState("");
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location]
  );
  const [showUnlinkAccount, setShowUnlinkAccount] = useState(false);
  const [showConnectFailed, setShowConnectFailed] = useState(false);
  const [connectSuccess, setConnectSuccess] = useState(false);

  const redirectURL = () => {
    const isValidToken =
      localStorage.getItem("pp-token") &&
      localStorage.getItem("pp-token") !== "";
    return isValidToken ? redirectLoginPage : "/";
  };

  useEffect(() => {
    if (queryParams && !pageLoaded?.current) {
      const isLogged = localStorage.getItem("logged") === "true";
      pageLoaded.current = true;
      const failureCode = queryParams.get("failureCode");
      const failedMessage = queryParams.get("failureMessage");
      const permissionDenied = failedMessage?.includes("Permission");
      const action = queryParams.get("action");
      setType(action as string);
      if (!failureCode && !failedMessage) {
        // Bypass show [Account connect] modal dialog in case of login action
        if (action === "login") {
          dispatch(getUser()); // will go to exams page when logged.
          window.location.href = redirectURL();
          return;
        }
        setConnectSuccess(true);
      } else {
        if (action === "login") {
          setShowUnlinkAccount(true);
          return;
        } else {
          // this 403 return when we cancel the login request
          if (failureCode === "403") {
            window.location.href = isLogged ? "/MyAccount" : redirectURL();
            return;
          }

          if (permissionDenied) {
            // facebook cancel case with failed message is 'Permission.'
            window.location.href = isLogged ? "/MyAccount" : redirectURL();
            return;
          } else {
            setShowConnectFailed(true);
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams, pageLoaded]);

  return (
    <>
      {connectSuccess && (
        <ConfirmModal
          icon={
            <div className="text-center m-tb-md">
              <img src={CheckConnectedIcon} alt="Icon" />
            </div>
          }
          header={<h4 className="text-center ">Account connected</h4>}
          show={connectSuccess}
          body={
            <>
              <div className="m-b-xxs text-center">
                You have successfully connected your account. For your next
                login, you can sign in using your social account.
              </div>
            </>
          }
          cancel={() => {
            setConnectSuccess(false);
            if (type == "connect") {
              window.location.href = "/MyAccount";
              return;
            }
          }}
          cancelBtnName="Close"
        />
      )}
      {showUnlinkAccount && (
        <ConfirmModal
          header={<h4 className="text-center ">Unlinked Account</h4>}
          show={showUnlinkAccount}
          body={
            <>
              <div className="m-b-xxs text-center">
                Your social account is not linked to a patient record. Please
                use your access link and log in with OTP.
              </div>
            </>
          }
          cancel={() => {
            setShowUnlinkAccount(false);
            window.location.href = redirectURL();
          }}
          cancelBtnName="Close"
        />
      )}
      {showConnectFailed && (
        <ConfirmModal
          header={<h4 className="text-center ">Account connection failed</h4>}
          show={showConnectFailed}
          body={
            <>
              <div className="m-b-xxs text-center">
                This account is currently linked to another patient. Please use
                a different account.
              </div>
            </>
          }
          cancel={() => {
            const isLogged = localStorage.getItem("logged") === "true";
            window.location.href = isLogged ? "/MyAccount" : redirectURL();
          }}
          cancelBtnName="Close"
        />
      )}
    </>
  );
};

export default OauthCallback;
